/**
 * Data Import Wizard — per-tab wizard state (S4.T1 foundation + S4.T3a Target draft-commit
 * machinery + S4.T3b Columns-step actions). Holds BOTH the wizard config (mirrors the persisted
 * `DataImportTabContent`) AND the live analysis — the analysis and merged column plan are
 * runtime-only, never persisted (`Docs/DataImport-Plan.md`).
 *
 * Column edits are tracked as `columnOverrides` (keyed by `sourceName`, NOT the analysis-assigned
 * `index`) so they survive both a re-analysis (revision refinement of the SAME file) and "Import
 * another file". `columns` is always DERIVED by re-applying `columnOverrides` + the last commit's
 * `projectedNativeTypes` on top of the latest `analysis.columns` — ONE source of truth per edit.
 *
 * Target-step edits are DRAFT (`draftTarget`) until `commitTarget` — the ONE atomic destination-
 * change transition (plan §2 Rev 10). Action BODIES live in `dataImportStore.actions.ts` (Target
 * draft-commit + shared entry helpers) and `dataImportStore.columnActions.ts` (Columns-step
 * actions) — extracted to keep this file at state-shape + wiring only.
 */
import { create } from 'zustand';
// Data Import Wizard contracts — imported from the ambient `electron.d.ts` re-export,
// NOT the electron/ path directly (see the re-export comment there for why).
import type {
  ImportAnalysis,
  ImportColumnPlan,
  ImportParseOptions,
  ImportRowFilter,
  ImportTargetColumn,
  ImportTargetMapping,
  ImportTargetPlan,
} from '../../../../types/electron';
import type {
  DataImportColumnOverride,
  DataImportStep,
  DataImportTabContent,
} from '../../core/types/workspaceTypes';
import { clearFileScopedFields, commitTargetAction, ensureEntry, ingestAnalysis } from './dataImportStore.actions';
import {
  hydrateEntryAction,
  importAnotherFileAction,
  removeTabEntries,
  reprojectNativeTypesAction,
  resetColumnOverridesAction,
  restoreColumnOverridesAction,
  setColumnOverrideAction,
  setIdentityColumnAction,
  setTruncateFirstAction,
} from './dataImportStore.columnActions';
import { clearMappingAction, setMappingAction, setMappingsAction } from './dataImportStore.mappingActions';
import {
  activeRunFor,
  markRunSettledEntries,
  markRunStartedEntries,
  type ActiveRuns,
} from './dataImportStore.runActions';

export type DataImportEntry = {
  filePath?: string;
  parse?: Partial<ImportParseOptions>;
  target?: ImportTargetPlan;
  /** Uncommitted Target-step edits (Rev 10). Genuinely partial mid-edit; absent
   * once it equals the committed `target` (or was never touched). Never persisted. */
  draftTarget?: Partial<ImportTargetPlan>;
  /** Engine-native default type per column `sourceName`, from the last `commitTarget`'s `projectTypes` call. */
  projectedNativeTypes?: Record<string, string>;
  /** One-shot post-commit transition payload; S4.T3b renders + clears it via `clearRematch`. */
  rematch?: { matched: number; total: number; newBlockers: number } | null;
  /** Set when Target was entered via the Columns command bar's "Edit" link (S4.T3b) — drives the footer's CTA label + Back target. */
  returnToColumns?: boolean;
  /** Set when the last `projectTypes` call (commit or reproject) rejected; cleared on the next success. Never persisted. */
  projectTypesError?: string | null;
  step: DataImportStep;
  columnOverrides: DataImportColumnOverride[];
  mappings?: ImportTargetMapping[];
  rowFilter?: ImportRowFilter;
  /** Live analysis snapshot (partial or final); revision-stamped, stale ones dropped. */
  analysis?: ImportAnalysis;
  /** `analysis.columns` with `columnOverrides` + `projectedNativeTypes` re-applied; [] until an analysis arrives. */
  columns: ImportColumnPlan[];
  /** Existing-table mode (S6.T2c-1): fetched target-column facts; undefined until a
   *  successful commit. Runtime-only — re-fetched on each commit, never persisted. */
  targetColumns?: ImportTargetColumn[];
  /** Existing-table mode: the collision-line row count; null = unknown (best-effort). Runtime-only. */
  existingRowCount?: number | null;
  /** Existing-table mode: set when the commit-time `fetchTargetColumns` call fails; cleared on the next success. Runtime-only. */
  targetColumnsError?: string;
};

type DataImportState = {
  entries: Record<string, DataImportEntry>;
  /** Live import-run markers keyed by tabId (finding #2). Set at Start, cleared when the run
   * settles or the tab unmounts. Read imperatively by the tab-close guard. Runtime-only. */
  activeRuns: ActiveRuns;
  /** Mark this tab's import run as live (called from `useRunStepActions` at Start). */
  markRunStarted: (tabId: string, operationId: string) => void;
  /** Clear this tab's live-run marker (run settled: done/cancelled/error, or unmount). */
  markRunSettled: (tabId: string) => void;
  /** The live-run marker for a tab, or undefined when no import is running there. */
  getActiveRun: (tabId: string) => { operationId: string } | undefined;
  /** Seed (or re-seed) a tab's entry from its persisted/restored tab content. */
  hydrate: (tabId: string, content?: DataImportTabContent) => void;
  setFilePath: (tabId: string, filePath: string | undefined) => void;
  setParseOptions: (tabId: string, parse: Partial<ImportParseOptions>) => void;
  setStep: (tabId: string, step: DataImportStep) => void;
  setReturnToColumns: (tabId: string, value: boolean) => void;
  setRowFilter: (tabId: string, rowFilter: ImportRowFilter | undefined) => void;
  /** Existing-table mapping mutations (S6.T2b) — bodies in `dataImportStore.mappingActions.ts`. */
  setMapping: (tabId: string, mapping: ImportTargetMapping) => void;
  clearMapping: (tabId: string, targetColumn: string) => void;
  setMappings: (tabId: string, mappings: ImportTargetMapping[]) => void;
  /** Upsert a column edit by sourceName; re-derives `columns` on top of the latest analysis. */
  setColumnOverride: (
    tabId: string,
    sourceName: string,
    patch: Partial<Omit<DataImportColumnOverride, 'sourceName'>>,
  ) => void;
  /** Draft-commit trio (Rev 10): merge a patch into `draftTarget` (seeded from `target`) / the ONE
   * atomic commit (projects native types, single `set()`) / discard back to the committed `target`. */
  setDraftTarget: (tabId: string, patch: Partial<ImportTargetPlan>) => void;
  commitTarget: (tabId: string) => Promise<void>;
  discardDraft: (tabId: string) => void;
  /** Clears the one-shot post-commit re-match payload. */
  clearRematch: (tabId: string) => void;
  /** Generated-PK toggle/name (S4.T3b): patches committed `target` directly — no-op if unset, never re-fires `rematch`. */
  setIdentityColumn: (tabId: string, patch: { addIdentityColumn?: boolean; identityColumnName?: string }) => void;
  /** Truncate-first toggle (S6.T2c-3, Import step): patches committed `target` directly — no-op if unset. */
  setTruncateFirst: (tabId: string, truncateFirst: boolean) => void;
  /** Clears every `columnOverrides` entry (keeps `projectedNativeTypes`); re-derives `columns`. */
  resetColumnOverrides: (tabId: string) => void;
  /** Bulk-restores `columnOverrides` wholesale (Undo for "Reset column changes", G1); re-derives `columns`. */
  restoreColumnOverrides: (tabId: string, overrides: DataImportColumnOverride[]) => void;
  /** Re-hydrates `projectedNativeTypes` for a restored session; never touches `target`/`rematch`. */
  reprojectNativeTypes: (tabId: string) => Promise<void>;
  /** Analysis ingestion (stale-revision guard, re-applies overrides); `importAnotherFile` keeps
   * parse prefs + column overrides for the next file; `removeTab` drops the entry on tab close. */
  applyAnalysis: (tabId: string, analysis: ImportAnalysis) => void;
  applyPartial: (tabId: string, analysis: ImportAnalysis) => void;
  importAnotherFile: (tabId: string) => void;
  removeTab: (tabId: string) => void;
};

export const useDataImportStore = create<DataImportState>((set, get) => ({
  entries: {},
  activeRuns: {},

  markRunStarted: (tabId, operationId) =>
    set((s) => ({ activeRuns: markRunStartedEntries(s.activeRuns, tabId, operationId) })),
  markRunSettled: (tabId) => set((s) => ({ activeRuns: markRunSettledEntries(s.activeRuns, tabId) })),
  getActiveRun: (tabId) => activeRunFor(get().activeRuns, tabId),

  hydrate: (tabId, content) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: hydrateEntryAction(s.entries, tabId, content) } })),

  setFilePath: (tabId, filePath) =>
    set((s) => {
      const entry = ensureEntry(s.entries, tabId);
      if (entry.filePath === filePath) return { entries: s.entries }; // no-op on identical path
      // C3: a NEW path must also clear the stale analysis (+ its analysisId/analysisComplete)
      // and derived columns — otherwise a fast Continue→Start after picking a new file would
      // import the PREVIOUS file's analysis while the UI shows the new path. `columnOverrides`
      // intentionally carries over (sourceName-keyed user prefs, re-applied on a matching header).
      // Finding #7: the file-scoped rowFilter/mappings/parse.sheetName are dropped too — a DIFFERENT
      // path is a different file whose columns the index-keyed filter/mappings would silently
      // mistarget (re-picking the SAME file no-ops above, so its filter survives).
      return {
        entries: {
          ...s.entries,
          [tabId]: { ...entry, ...clearFileScopedFields(entry), filePath, analysis: undefined, columns: [] },
        },
      };
    }),

  setParseOptions: (tabId, parse) =>
    set((s) => {
      const entry = ensureEntry(s.entries, tabId);
      return { entries: { ...s.entries, [tabId]: { ...entry, parse: { ...entry.parse, ...parse } } } };
    }),

  setStep: (tabId, step) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: { ...ensureEntry(s.entries, tabId), step } } })),

  setReturnToColumns: (tabId, value) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: { ...ensureEntry(s.entries, tabId), returnToColumns: value } } })),

  setRowFilter: (tabId, rowFilter) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: { ...ensureEntry(s.entries, tabId), rowFilter } } })),

  setMapping: (tabId, mapping) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: setMappingAction(ensureEntry(s.entries, tabId), mapping) } })),
  clearMapping: (tabId, targetColumn) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: clearMappingAction(ensureEntry(s.entries, tabId), targetColumn) } })),
  setMappings: (tabId, mappings) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: setMappingsAction(ensureEntry(s.entries, tabId), mappings) } })),

  setColumnOverride: (tabId, sourceName, patch) =>
    set((s) => ({
      entries: { ...s.entries, [tabId]: setColumnOverrideAction(ensureEntry(s.entries, tabId), sourceName, patch) },
    })),

  setDraftTarget: (tabId, patch) =>
    set((s) => {
      const entry = ensureEntry(s.entries, tabId);
      const base = entry.draftTarget ?? entry.target;
      // Any draft edit clears the previous commit's transient error (Kimi #9): a
      // failed `projectTypes`/`fetchTargetColumns` attempt must not keep shouting
      // "Couldn't read …" once the user has moved on to a different, possibly
      // valid, destination — `TargetStep` gives these top footer precedence.
      return {
        entries: {
          ...s.entries,
          [tabId]: { ...entry, draftTarget: { ...base, ...patch }, projectTypesError: null, targetColumnsError: undefined },
        },
      };
    }),

  discardDraft: (tabId) =>
    set((s) => ({
      entries: {
        ...s.entries,
        // Discarding the edits reverts to the committed target — the transient
        // commit error was about the abandoned attempt, so clear it too (Kimi #9).
        [tabId]: { ...ensureEntry(s.entries, tabId), draftTarget: undefined, projectTypesError: null, targetColumnsError: undefined },
      },
    })),

  clearRematch: (tabId) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: { ...ensureEntry(s.entries, tabId), rematch: null } } })),

  setIdentityColumn: (tabId, patch) =>
    set((s) => ({
      entries: { ...s.entries, [tabId]: setIdentityColumnAction(ensureEntry(s.entries, tabId), patch) },
    })),

  setTruncateFirst: (tabId, truncateFirst) =>
    set((s) => ({
      entries: { ...s.entries, [tabId]: setTruncateFirstAction(ensureEntry(s.entries, tabId), truncateFirst) },
    })),

  resetColumnOverrides: (tabId) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: resetColumnOverridesAction(ensureEntry(s.entries, tabId)) } })),

  restoreColumnOverrides: (tabId, overrides) =>
    set((s) => ({
      entries: { ...s.entries, [tabId]: restoreColumnOverridesAction(ensureEntry(s.entries, tabId), overrides) },
    })),

  reprojectNativeTypes: (tabId) => {
    const entry = ensureEntry(get().entries, tabId);
    return reprojectNativeTypesAction(tabId, entry, (build) =>
      set((s) => ({ entries: { ...s.entries, [tabId]: build(ensureEntry(s.entries, tabId)) } })),
    );
  },

  commitTarget: (tabId) => {
    const entry = ensureEntry(get().entries, tabId);
    return commitTargetAction(entry, (build) =>
      set((s) => ({ entries: { ...s.entries, [tabId]: build(ensureEntry(s.entries, tabId)) } })),
    );
  },

  applyAnalysis: (tabId, analysis) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: ingestAnalysis(ensureEntry(s.entries, tabId), analysis) } })),

  applyPartial: (tabId, analysis) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: ingestAnalysis(ensureEntry(s.entries, tabId), analysis) } })),

  importAnotherFile: (tabId) =>
    set((s) => ({ entries: { ...s.entries, [tabId]: importAnotherFileAction(ensureEntry(s.entries, tabId)) } })),

  removeTab: (tabId) =>
    set((s) => ({ entries: removeTabEntries(s.entries, tabId), activeRuns: markRunSettledEntries(s.activeRuns, tabId) })),
}));
